import React, {useEffect, useState} from "react";
import {Card, Col, DatePicker, Progress, Result, Row, Select, Space, Statistic, Tabs} from "antd";
import dayjs from "dayjs";
import axios from "axios";
import {httpRequest} from "../services/httpRequest";
import {GrantData} from "../reports/GrantData";

export const Analytics = ({history, location}: any) => {
    const queryParams = new URLSearchParams(location.search || (location.hash.includes('?') ? location.hash.split('?')[1] : ''));
    const initialTab = queryParams.get('tab') || '1';
    const initialFrom = queryParams.get('from') ? dayjs(queryParams.get('from')) : dayjs().startOf('M');
    const initialTo = queryParams.get('to') ? dayjs(queryParams.get('to')) : dayjs().endOf('M');

    const [month, setMonth] = useState(initialFrom);
    const [to, setTo] = useState(initialTo);
    const [activeTab, setActiveTab] = useState(initialTab);

    const [analytics, setAnalytics] = useState<any>({});
    const [contactData, setContactData] = useState<any>({});
    const [checkInData, setCheckInData] = useState<any>({});
    const [youthData, setYouthData] = useState<any>({});
    const [ntData, setNtData] = useState<any>({});
    const [grantData, setGrantData] = useState<any>([]);
    const [resources, setResources] = useState<any>([]);
    const [resourceData, setResourceData] = useState<any>(null);
    const [selectedResource, setSelectedResource] = useState<any>(null);

    const [loadingContactData, setLoadingContactData] = useState(false);
    const [loadingCheckIns, setLoadingCheckIns] = useState(false);
    const [loadingYouthData, setLoadingYouthData] = useState(false);
    const [loadingNtData, setLoadingNtData] = useState(false);
    const [loadingGrantData, setLoadingGrantData] = useState(false);
    const [loadingResources, setLoadingResources] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search || (location.hash.includes('?') ? location.hash.split('?')[1] : ''));
        let changed = false;
        if (params.get('tab') !== activeTab) {
            params.set('tab', activeTab);
            changed = true;
        }
        const fromStr = month ? month.toISOString() : '';
        if (params.get('from') !== fromStr) {
            if (fromStr) params.set('from', fromStr);
            else params.delete('from');
            changed = true;
        }
        const toStr = to ? to.toISOString() : '';
        if (params.get('to') !== toStr) {
            if (toStr) params.set('to', toStr);
            else params.delete('to');
            changed = true;
        }

        if (changed) {
            history.replace({
                search: params.toString()
            });
        }
    }, [activeTab, month, to, history, location.search, location.hash]);

    const [loading, setLoading] = useState(false);

    const fetchOverviewData = () => {
        if (!month || !to) return;

        setLoading(true);
        setLoadingContactData(true);
        setLoadingCheckIns(true);
        setLoadingYouthData(true);

        axios.get(`/api/v1/analytics?from=${month.toISOString()}&to=${to.toISOString()}`,
            httpRequest.getHeaders())
            .then(response => {
                setAnalytics(response.data);
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => {
                setLoading(false);
            });
        axios.get(`/api/v1/analytics?from=${month.toISOString()}&to=${to.toISOString()}&type=contact-data`,
            httpRequest.getHeaders())
            .then(response => {
                setContactData(response.data)
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => {
                setLoadingContactData(false);
            });
        axios.get(`/api/v1/analytics?from=${month.toISOString()}&to=${to.toISOString()}&type=check-ins`,
            httpRequest.getHeaders())
            .then(response => {
                setCheckInData(response.data)
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => {
                setLoadingCheckIns(false);
            });
        axios.get(`/api/v1/analytics?from=${month.toISOString()}&to=${to.toISOString()}&type=youth-data`,
            httpRequest.getHeaders())
            .then(response => {
                setYouthData(response.data)
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => {
                setLoadingYouthData(false);
            });
    };

    const fetchNtData = () => {
        if (!month || !to) return;

        setLoadingNtData(true);

        axios.get(`/api/v1/analytics?from=${month.toISOString()}&to=${to.toISOString()}&type=nt-data`,
            httpRequest.getHeaders())
            .then(response => {
                setNtData(response.data)
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => {
                setLoadingNtData(false);
            });
    };

    const fetchGrantData = () => {
        if (!month || !to) return;

        setLoadingGrantData(true);

        axios.get(`/api/v1/analytics?from=${month.toISOString()}&to=${to.toISOString()}&type=grant-data`,
            httpRequest.getHeaders())
            .then(response => {
                setGrantData(response.data)
            })
            .catch(error => httpRequest.errorHandler(error, history))
            .finally(() => {
                setLoadingGrantData(false);
            });
    };

    const fetchServiceData = () => {
        if (selectedResource && month && to) {
            setLoadingResources(true);

            axios.get(`/api/v1/analytics/${selectedResource}/resources?from=${month.toISOString()}&to=${to.toISOString()}`,
                httpRequest.getHeaders())
                .then(response => {
                    setResourceData(response.data);
                })
                .catch(error => httpRequest.errorHandler(error, history))
                .finally(() => {
                    setLoadingResources(false);
                });
        }
    };

    useEffect(() => {
        if (activeTab === '1') {
            fetchOverviewData();
        } else if (activeTab === '2') {
            fetchNtData();
        } else if (activeTab === '3') {
            fetchGrantData();
        } else if (activeTab === '4') {
            fetchServiceData();
        }
    }, [activeTab, month, to, selectedResource]);

    useEffect(() => {
        axios.get(`api/v1/resource-list/list`,
            httpRequest.getHeaders())
            .then(response => {
                setResources(response.data);
            })
            .catch(error => httpRequest.errorHandler(error, history))
    }, []);

    return (
        <Col xs={24}>
            <Card title={`Reports`}
                  className={'mt-3'}
                  extra={[
                      <DatePicker.RangePicker
                          key={'range'}
                          allowClear={false}
                          picker="month"
                          format={`MMM - 'YY`}
                          value={[month, to]}
                          onChange={(values) => {
                              const [start, end] = values || [];
                              setMonth(start || null as any);
                              setTo(end?.endOf('month') || null as any);
                          }}
                      />
                  ]}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: '1',
                            label: 'Overview',
                            children: (
                                <Row gutter={16}>
                                    <Col xs={24} md={12} xl={8} className={'mb-2'}>
                                        <Card loading={loadingCheckIns} title={'Youth Check-Ins'}>
                                            <Space size={'large'} align={'center'}>
                                                <Statistic
                                                    title={checkInData?.header}
                                                    value={checkInData?.value}
                                                    valueStyle={{color: getColor(checkInData?.value)}}
                                                />
                                                <Statistic
                                                    title={'Un-Duplicated'}
                                                    value={checkInData?.uniqueValue}
                                                    valueStyle={{color: getColor(checkInData?.uniqueValue)}}
                                                />
                                            </Space>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={12} xl={8} className={'mb-2'}>
                                        <Card loading={loadingYouthData} title={'Youth New to Lyte'}>
                                            <Statistic
                                                title={youthData?.header}
                                                value={youthData?.value}
                                                valueStyle={{color: getColor(youthData?.value)}}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={12} xl={8} className={'mb-2'}>
                                        <Card loading={loading} title={'Services'}>
                                            <Space size={'large'} align={'center'}>
                                                <Statistic
                                                    title={analytics.totalActivities?.header}
                                                    value={analytics.totalActivities?.value}
                                                    valueStyle={{color: getColor(analytics.totalActivities?.value)}}
                                                />
                                                <Statistic
                                                    title={analytics.uniqueActivities?.header}
                                                    value={analytics.uniqueActivities?.value}
                                                    valueStyle={{color: getColor(analytics.uniqueActivities?.value)}}
                                                />
                                            </Space>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={12} xl={8} className={'mb-2'}>
                                        <Card loading={loadingContactData} title={'Contacts'}>
                                            <Space size={'large'} align={'center'}>
                                                <Statistic
                                                    title={contactData.totalYouth?.header}
                                                    value={contactData.totalYouth?.value}
                                                    valueStyle={{color: getColor(contactData.totalYouth?.value)}}
                                                />
                                                <Statistic
                                                    title={contactData.uniqueYouth?.header}
                                                    value={contactData.uniqueYouth?.value}
                                                    valueStyle={{color: getColor(contactData.uniqueYouth?.value)}}
                                                />
                                            </Space>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={12} xl={8} className={'mb-2'}>
                                        <Card title={'Top Services'} loading={loading}>
                                            {analytics.topActivities?.data.map((activity: any) => {
                                                return (
                                                    <Progress
                                                        key={activity.name}
                                                        className={'mb-2'}
                                                        percent={(activity.value / (analytics.totalActivities.value || 100)) * 100}
                                                        format={() => `${activity.name} - ${activity.value}`}
                                                        percentPosition={{align: 'start', type: 'outer'}}
                                                        size={['100%', 15]}
                                                        strokeColor={`#${activity.color}`}
                                                    />
                                                )
                                            })}
                                            {analytics.topActivities?.data.length === 0 &&
                                                <Result
                                                    title="No data to display for this time period."
                                                />
                                            }
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={12} xl={8} className={'mb-2'}>
                                        <Card title={'Youth Contacts by Sexual Orientation'} loading={loadingContactData}>
                                            {contactData.youthData?.data.map((activity: any) => {
                                                return (
                                                    <Progress
                                                        key={activity.name}
                                                        className={'mb-2'}
                                                        percent={(activity.value / (contactData.totalYouth?.value || 100)) * 100}
                                                        format={() => `${activity.name} - ${activity.value}`}
                                                        percentPosition={{align: 'start', type: 'outer'}}
                                                        size={['100%', 15]}
                                                        strokeColor={`#${activity.color}`}
                                                    />
                                                )
                                            })}
                                            {contactData.youthData?.data.length === 0 &&
                                                <Result
                                                    title="No data to display for this time period."
                                                />
                                            }
                                        </Card>
                                    </Col>
                                </Row>
                            )
                        },
                        {
                            key: '2',
                            label: 'Un-duplicated Youth Data',
                            children: (
                                <Row gutter={16}>
                                    <Col xs={24}>
                                        <Card loading={loadingNtData} title={'Un-duplicated Youth Data (16-24 year olds)'}>
                                            <Row gutter={8}>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.totalContacts?.header}
                                                        value={ntData.totalContacts?.value}
                                                        valueStyle={{color: getColor(ntData.totalContacts?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.educationSupport?.header}
                                                        value={ntData.educationSupport?.value}
                                                        valueStyle={{color: getColor(ntData.educationSupport?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.educationEconomicSupport?.header}
                                                        value={ntData.educationEconomicSupport?.value}
                                                        valueStyle={{color: getColor(ntData.educationEconomicSupport?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.housingSupport?.header}
                                                        value={ntData.housingSupport?.value}
                                                        valueStyle={{color: getColor(ntData.housingSupport?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.medicaid?.header}
                                                        value={ntData.medicaid?.value}
                                                        valueStyle={{color: getColor(ntData.medicaid?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.healthServices?.header}
                                                        value={ntData.healthServices?.value}
                                                        valueStyle={{color: getColor(ntData.healthServices?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.benefitsEnrollment?.header}
                                                        value={ntData.benefitsEnrollment?.value}
                                                        valueStyle={{color: getColor(ntData.benefitsEnrollment?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.mealOrPantry?.header}
                                                        value={ntData.mealOrPantry?.value}
                                                        valueStyle={{color: getColor(ntData.mealOrPantry?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.mealAndPantry?.header}
                                                        value={ntData.mealAndPantry?.value}
                                                        valueStyle={{color: getColor(ntData.mealAndPantry?.value)}}
                                                    />
                                                </Col>
                                                <Col xs={12} md={12} xl={6}>
                                                    <Statistic
                                                        title={ntData.fullBenefits?.header}
                                                        value={ntData.fullBenefits?.value}
                                                        valueStyle={{color: getColor(ntData.fullBenefits?.value)}}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>
                            )
                        },
                        {
                            key: '3',
                            label: 'Grant Data',
                            children: (
                                <Row gutter={16}>
                                    <GrantData loading={loadingGrantData} data={grantData} />
                                </Row>
                            )
                        },
                        {
                            key: '4',
                            label: 'Service Data',
                            children: (
                                <Row gutter={16}>
                                    <Col xs={24}>
                                        <Card title={'Service Data'} loading={loadingResources}
                                              extra={<Select
                                                  placeholder={'Select a service'}
                                                  style={{width: '100%'}}
                                                  showSearch
                                                  optionFilterProp={'label'}
                                                  defaultValue={selectedResource}
                                                  onChange={setSelectedResource}
                                                  options={resources.map((item: any) => {
                                                      return {
                                                          label: item.name,
                                                          value: item.id
                                                      }
                                                  })}/>}
                                        >
                                            {resourceData &&
                                                <Statistic
                                                    loading={loadingResources}
                                                    title={resources.find((res: any) => res.id === selectedResource).name}
                                                    value={resourceData.value}
                                                    valueStyle={{color: getColor(resourceData.value)}}
                                                />
                                            }
                                            {!resourceData &&
                                                <Result
                                                    title="No data to display for this time period/resource combination."
                                                />
                                            }
                                        </Card>
                                    </Col>
                                </Row>
                            )
                        }
                    ]}
                />
            </Card>
        </Col>
    )
}
export const getColor = (currentValue: any) => {
    if (currentValue > 100)
        return '#3f8600';
    if (currentValue < 10)
        return '#cf1322'
    return '#585858';
}